'use client'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import {
    addCartItemService,
    getCurrentCartService,
    removeCartItemService,
    updateCartItemService,
} from '@/services/domain/cart/CartService'
import { useCartStore } from '@/stores/useCartStore'
import type { ICart, ICartItem } from '~/schemas/cart/cart'
import { useCartCache } from './useCartCache'

/**
 * Carrinho da loja. A fonte da verdade é a `useCartStore` (persistida, para o carrinho aparecer na hora), gravada pelos
 * services; o React Query revalida pelo `cart/current` ao montar e ao voltar o foco da aba, e as ações atualizam o
 * cache. Navegação e alertas de produto ("Produto adicionado") ficam com a tela.
 */

const EMPTY: ICartItem[] = []

// ===== CARRINHO ATUAL =====

export function useCart({ enabled = true }: { enabled?: boolean } = {}) {
    const cart: ICart | null = useCartStore((state) => state.data)

    const query = useQuery({
        queryKey: ['cart', 'current'],
        queryFn: getCurrentCartService,
        enabled,
        refetchOnWindowFocus: true,
    })

    const items = cart?.cartItems ?? EMPTY
    const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items])

    return {
        cart,
        items,
        /** Soma das quantidades (o contador do ícone do carrinho). */
        itemCount,
        isEmpty: items.length === 0,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
    }
}

// ===== ADICIONAR =====

export function useAddToCart() {
    const { setCartCache, handleCartError } = useCartCache()

    const mutation = useMutation({
        mutationFn: addCartItemService,
        onSuccess: (cart) => setCartCache(cart),
        onError: (error) => handleCartError(error),
    })

    /** Resolve com o carrinho novo; rejeita com o ApiError (produto repetido, `stockError`, `productNotFound`). */
    const addToCart = useCallback(
        (productId: string, quantity = 1) => mutation.mutateAsync({ productId, quantity }),
        [mutation.mutateAsync],
    )

    return { addToCart, isAdding: mutation.isPending }
}

// ===== ALTERAR QUANTIDADE =====

export function useUpdateCartItem() {
    const { setCartCache, handleCartError } = useCartCache()

    const mutation = useMutation({
        mutationFn: updateCartItemService,
        onSuccess: (cart) => setCartCache(cart),
        onError: (error) => handleCartError(error),
    })

    const updateQuantity = useCallback(
        (productId: string, quantity: number) => mutation.mutateAsync({ productId, quantity }),
        [mutation.mutateAsync],
    )

    return { updateQuantity, isUpdating: mutation.isPending }
}

// ===== REMOVER =====

export function useRemoveCartItem() {
    const { setCartCache, handleCartError } = useCartCache()

    const mutation = useMutation({
        mutationFn: removeCartItemService,
        onSuccess: (cart) => {
            setCartCache(cart)
            toast.success('Produto removido com sucesso', { autoClose: 2000 })
        },
        onError: (error) => handleCartError(error),
    })

    const removeItem = useCallback(
        (productId: string, quantity = 0) => mutation.mutateAsync({ productId, quantity }),
        [mutation.mutateAsync],
    )

    return { removeItem, isRemoving: mutation.isPending }
}
